import { format, parseISO } from 'date-fns';

// ===== NAME FORMATTING =====
export const formatFullName = (person) => {
  if (!person) return '';
  return `${person.firstName || ''} ${person.lastName || ''}`.trim();
};

// ===== PHONE FORMATTING =====
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

// ===== CURRENCY =====
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

// ===== DATE FORMATTING =====
export const formatDate = (date, formatStr = 'MMM d, yyyy') => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, formatStr);
  } catch {
    return date;
  }
};

// ===== ADDRESS =====
export const formatAddress = (property) => {
  if (!property) return '';
  const parts = [
    property.addressLine1,
    property.city,
    property.state,
    property.zip
  ].filter(Boolean);
  return parts.join(', ');
};

export const formatShortAddress = (property) => {
  if (!property) return '';
  return `${property.addressLine1}, ${property.city}`;
};

// ===== ESTIMATE HELPERS =====
/**
 * Single source of truth: is this a custom (non-residential-calculator) estimate?
 */
export const isCustomEstimate = (estimate) => {
  if (!estimate) return false;
  return (
    estimate.source === 'custom_estimate_builder' ||
    estimate.serviceType === 'commercial' ||
    estimate.serviceType === 'hoarding' ||
    estimate.serviceType === 'junk_removal' ||
    estimate.serviceType === 'custom' ||
    (estimate.lineItems?.length > 0 && !estimate.calculatorInputs?.sqft && !estimate.calculatorInputs?.condition)
  );
};

/**
 * Resolve the display price for an estimate regardless of type.
 * Checks displayPrice first (new normalized field), then falls back to legacy fields.
 */
export const resolveEstimatePrice = (estimate) => {
  if (!estimate) return 0;
  // New normalized field (written on save for all new estimates)
  if (estimate.displayPrice) return estimate.displayPrice;
  // Legacy fields
  if (estimate.approvedRecurringTotal) return estimate.approvedRecurringTotal;
  if (estimate.recurringTotal) return estimate.recurringTotal;
  if (estimate.totalPrice) return estimate.totalPrice;
  if (estimate.total) return estimate.total;
  if (estimate.price) return estimate.price;
  if (estimate.basePrice) return estimate.basePrice;
  if (estimate.lineItems?.length) return estimate.lineItems.reduce((s, li) => s + (li.amount || li.total || li.price || 0), 0);
  return 0;
};

/**
 * Compute a displayPrice from line items (new format) or legacy fields.
 * Used by AppContext on save to write the normalized field.
 */
export const computeDisplayPrice = (estimateData) => {
  if (!estimateData) return 0;

  const lineItems = estimateData.lineItems;
  if (lineItems?.length > 0) {
    // New line item format: find the default selected recurring option
    const selectedRecurring = lineItems.find(
      li => li.group === 'recurring_options' && li.selected
    );
    if (selectedRecurring) {
      // Sum: selected recurring + its selected add-ons
      let price = selectedRecurring.price || 0;
      if (selectedRecurring.addOns?.length) {
        price += selectedRecurring.addOns
          .filter(ao => ao.selected || ao.required)
          .reduce((s, ao) => s + (ao.price || 0), 0);
      }
      return Math.round(price * 100) / 100;
    }

    // No recurring group — sum all selected/required items (custom estimates)
    return Math.round(
      lineItems
        .filter(li => li.selected || li.required)
        .reduce((sum, li) => {
          let itemTotal = li.price || 0;
          if (li.addOns?.length) {
            itemTotal += li.addOns
              .filter(ao => ao.selected || ao.required)
              .reduce((s, ao) => s + (ao.price || 0), 0);
          }
          return sum + itemTotal;
        }, 0) * 100
    ) / 100;
  }

  // Fallback to legacy fields
  if (estimateData.approvedRecurringTotal) return estimateData.approvedRecurringTotal;
  if (estimateData.recurringTotal) return estimateData.recurringTotal;
  if (estimateData.total) return estimateData.total;
  return 0;
};

/**
 * Resolve client name from estimate + client record
 */
export const resolveClientName = (estimate, client) => {
  if (client) return formatFullName(client);
  if (estimate?.clientName) return estimate.clientName;
  return 'Unknown Client';
};

/**
 * Resolve property address from estimate + property record
 */
export const resolveAddress = (estimate, property) => {
  if (property?.addressLine1) return `${property.addressLine1}, ${property.city || ''}`;
  if (estimate?.propertyAddress) return estimate.propertyAddress;
  if (estimate?.address) return estimate.address;
  if (estimate?.tempPropertyData?.addressLine1) return `${estimate.tempPropertyData.addressLine1}, ${estimate.tempPropertyData.city || ''}`;
  if (estimate?.propertyInfo?.street) return `${estimate.propertyInfo.street}, ${estimate.propertyInfo.city || ''}`;
  return 'No address';
};

/**
 * Get lead status from estimate status.
 * Updated for expanded pipeline: new → contacted → walkthrough_scheduled → estimate_in_progress → estimate_sent → follow_up → won / lost
 */
export const getLeadStatus = (estimate) => {
  if (!estimate) return 'new';
  // Explicit leadStatus always wins
  if (estimate.leadStatus) return estimate.leadStatus;
  // Map from estimate status for records without explicit leadStatus
  if (estimate.status === 'pending_review') return 'new';
  if (estimate.status === 'draft') {
    // If calculator inputs exist, they're working on it
    return estimate.calculatorInputs ? 'estimate_in_progress' : 'new';
  }
  if (estimate.status === 'sent') return 'estimate_sent';
  if (estimate.status === 'approved' || estimate.status === 'converted') return 'won';
  if (estimate.status === 'declined') return 'lost';
  if (estimate.status === 'expired') return 'follow_up';
  return 'new';
};

/**
 * Get service type label from estimate
 */
export const getServiceLabel = (estimate) => {
  const st = estimate?.serviceType || estimate?.estimateType || '';
  const labels = {
    'one_time': 'One-Time Clean', 'deep_clean': 'Deep Clean', 'recurring': 'Recurring',
    'move_out': 'Move-Out', 'move_in': 'Move-In', 'move_in_out': 'Move In/Out', 'commercial': 'Commercial',
    'carpet': 'Carpet Clean', 'window': 'Window Clean', 'pressure_wash': 'Pressure Wash',
    'organizing': 'Organizing', 'hoarding': 'Hoarding', 'junk_removal': 'Junk Removal',
    'custom': 'Custom',
  };
  return labels[st] || st || 'Cleaning';
};

/**
 * Build a Google Calendar event URL
 */
export const buildGoogleCalendarUrl = ({ title, startDate, startTime, durationMinutes = 60, location, description }) => {
  // Format date/time to Google Calendar's expected format: YYYYMMDDTHHmmss
  const start = new Date(`${startDate}T${startTime || '09:00'}`);
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

  const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${fmt(start)}/${fmt(end)}`,
    details: description || '',
    location: location || '',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// ===== SEARCH =====
export const searchFilter = (items, searchTerm, fields) => {
  if (!searchTerm) return items;
  const term = searchTerm.toLowerCase();
  
  return items.filter(item => {
    return fields.some(field => {
      const value = typeof field === 'function' ? field(item) : item[field];
      return value && String(value).toLowerCase().includes(term);
    });
  });
};
